import { TypedDocumentNode, gql } from '@apollo/client';
import { useEffect, useState } from 'react';

import { useTypedParams } from '@sorare/routing';

import { GameStatus } from '@sorare/core/src/__generated__/globalTypes';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { MLB_SCOUTING_CENTER_SCORES } from '@sorare/core/src/constants/__generated__/routes';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { navLabels } from '@sorare/core/src/lib/glossary';
import { isType, randomizedPollInterval } from '@sorare/core/src/lib/gql';

import { GameView } from 'components/game/GameView';

import type {
  MLBScoreGameIdQuery,
  MLBScoreGameIdQueryVariables,
} from './__generated__/page.graphql';
import { PageParams } from './__generated__/routeParams';

const query = gql`
  query MLBScoreGameIdQuery($gameId: ID!, $gameIdWithoutPrefix: ID!) {
    anyGame(id: $gameId) {
      id
      statusTyped
      ...GameView_GameOfBaseball
    }
  }
  ${GameView.fragments.GameOfBaseball}
` as TypedDocumentNode<MLBScoreGameIdQuery, MLBScoreGameIdQueryVariables>;

export const MLBScoreGameIdPage = () => {
  const { gameId } = useTypedParams<PageParams>();

  const [pollInterval, setPollInterval] = useState(0);

  const { data } = useQuery(query, {
    variables: {
      gameId: `Game:${idFromObject(gameId)}`,
      gameIdWithoutPrefix: idFromObject(gameId),
    },
    skip: !gameId,
    pollInterval,
  });

  const anyGame = data?.anyGame;

  // Set pollInterval only if game is playing
  useEffect(() => {
    if (anyGame?.statusTyped === GameStatus.playing) {
      setPollInterval(randomizedPollInterval(0.5));
    } else {
      setPollInterval(0);
    }
  }, [anyGame?.statusTyped]);

  if (!anyGame) {
    return <LoadingIndicator fullHeight />;
  }

  if (!isType(anyGame, 'GameOfBaseball')) {
    return null;
  }

  return (
    <>
      <PageHeader text={navLabels.scores} backTo={MLB_SCOUTING_CENTER_SCORES} />
      <GameView game={anyGame} />
    </>
  );
};

export default MLBScoreGameIdPage;
